/**
 * The shared coarse-grained bottom navigation bar — the app's one navigation
 * component.
 *
 * A mobile-first Home · Capture · Docs · Insights · Connect bar, promoted from
 * the capture surface and extended with a Connect button for the /connect pillar
 * (its landing leads with connecting an AI to SystemDox — MCP install, the
 * Agent Skills export, and a live preview). Home points at the hub: the logo
 * also links there, but this is the discoverable affordance — without it the
 * hub is the one page the nav cannot reach. It renders inside TenantShell and
 * ReaderShell, so it is identical on every route by construction — the "one
 * bar everywhere" invariant holds.
 *
 * Every destination lives on this same tenant root host ({slug}.systemdox.com)
 * now, so these are in-SPA NavLinks, not cross-host jumps. Shown on every screen
 * size — it is the primary nav on all sizes. A destination is "current" for its
 * own path and any descendant (so /connect stays lit across its sub-routes);
 * Home matches only "/" exactly, or it would light up everywhere.
 *
 * Icons are the SystemDox family (components/icons/SdxIcon), not stock lucide.
 */

import { Link, useLocation } from 'react-router-dom';
import {
  IconAdd,
  IconConnect,
  IconDocs,
  IconGap,
  IconHome,
} from '@/components/icons/SdxIcon';
import { cn } from '@/lib/utils';

const ITEMS = [
  { label: 'Home', icon: IconHome, to: '/' },
  { label: 'Capture', icon: IconAdd, to: '/add' },
  { label: 'Docs', icon: IconDocs, to: '/docs' },
  { label: 'Insights', icon: IconGap, to: '/insights' },
  { label: 'Connect', icon: IconConnect, to: '/connect' },
] as const;

const itemCls =
  'flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors';
const idleCls =
  'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200';
const activeCls = 'text-blue-600 dark:text-blue-400';

export function BottomNav() {
  const { pathname } = useLocation();
  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  return (
    <nav className="shrink-0 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur">
      <div className="mx-auto flex max-w-lg items-stretch">
        {ITEMS.map(({ label, icon: Icon, to }) => {
          const active = isActive(to);
          return (
            <Link
              key={label}
              to={to}
              aria-current={active ? 'page' : undefined}
              className={cn(itemCls, active ? activeCls : idleCls)}
            >
              <Icon className="w-5 h-5" />
              {label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
